import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import {
    BilledOfferUnbilledCountInput,
    OkObject,
    SocialUnbilledWorksCountObject,
    WorkGridObject,
} from '@it2go/types';
import { GridInput } from '@it2go/data-grid';
import {
    SocialBillingDocumentGridObject,
    SocialBillingDocumentMonthCountObject,
    SocialBillingDocumentStatusObject,
} from '@libs/types/src/services/object-type/billing/social-billing-document.object';
import {
    BilledOfferNewBillingAvailabilityObject,
} from '@libs/types/src/services/object-type/billing/billed-offer-new-billing-availability.object';
import {
    BilledOfferCreatePartialBillingInput,
} from '@libs/types/src/services/input-type/billing/billed-offer.create-partial-billing-input';
import {
    BilledOfferCreateBillingInput,
} from '@libs/types/src/services/input-type/billing/billed-offer.create-billing-input';
import {
    BilledOfferRemainingBillingAvailabilityObject,
} from '@libs/types/src/services/object-type/billing/billed-offer-remaining-billing-availability.object';
import {
    BilledOfferCreateRemainingBillingInput,
} from '@libs/types/src/services/input-type/billing/billed-offer.create-remaining-billing-input';
import {
    SocialBillingReceiptGridObject,
} from '@libs/types/src/services/object-type/billing/social-billing-receipt.object';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import {
    SocialBillingReceiptCancellationInput,
} from '@libs/types/src/services/input-type/billing/social-billing-receipt.cancellation-input';
import {
    SocialBillingReceiptPrintInput,
} from '@libs/types/src/services/input-type/billing/social-billing-receipt.print-input';
import {
    SocialBillingReceiptCreateInput,
} from '@libs/types/src/services/input-type/billing/social-billing-receipt.create-input';
import { SocialBillingReceiptSendInput } from '@libs/types/src/services/input-type/billing/social-billing-receipt.send-input';
import { filterFrag } from './types/filter.types';
import {
    SocialBillingDocumentRemoveItemInput,
} from '@libs/types/src/services/input-type/billing/social-billing-document-remove-item.input';
import {
    SocialBillingDocumentAddMissingItemsInput,
} from '@libs/types/src/services/input-type/billing/social-billing-document-add-missing-items.input';
import {
    SocialBillingDocumentPrintInput,
} from '@libs/types/src/services/input-type/billing/social-billing-document.print-input';
import { DebtorsGridObject } from '@libs/types/src/services/object-type/dashboard/debtors.object';

@Injectable({ providedIn: 'root' })
export class SocialBillingGqlService extends GqlServiceV2 {

    public getSocialAdvanceBillingCandidates(input: GridInput, fragment: string): Promise<WorkGridObject> {
        return this.query<WorkGridObject>(
            `
                query($input: GridInput!) {
                    billedOffer {
                        advanceBillingCandidates(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getBilledOfferUnbilledCount(input: BilledOfferUnbilledCountInput, fragment: string): Promise<SocialUnbilledWorksCountObject> {
        return this.query<SocialUnbilledWorksCountObject>(
            `
                query($input: BilledOfferUnbilledCountInput!) {
                    billedOffer {
                        unbilledCount(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialNewBillingAvailability(input: BilledOfferUnbilledCountInput, fragment: string): Promise<BilledOfferNewBillingAvailabilityObject> {
        return this.query<BilledOfferNewBillingAvailabilityObject>(
            `
                query($input: BilledOfferUnbilledCountInput!) {
                    billedOffer {
                        newBillingAvailability(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialRemainingBillingAvailability(
        input: BilledOfferUnbilledCountInput,
        fragment: string,
    ): Promise<BilledOfferRemainingBillingAvailabilityObject> {
        return this.query<BilledOfferRemainingBillingAvailabilityObject>(
            `
                query($input: BilledOfferUnbilledCountInput!) {
                    billedOffer {
                        remainingBillingAvailability(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialBillingDocuments(input: GridInput, fragment: string): Promise<SocialBillingDocumentGridObject> {
        return this.query<SocialBillingDocumentGridObject>(
            `
                query($input: GridInput!) {
                    socialBillingDocument {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialBillingDocumentDebtors(input: GridInput, fragment: string): Promise<DebtorsGridObject> {
        return this.query<DebtorsGridObject>(
            `
                query($input: GridInput!) {
                    socialBillingDocument {
                        debtors(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialBillingDocumentStatus(input: IdInput, fragment: string): Promise<SocialBillingDocumentStatusObject> {
        return this.query<SocialBillingDocumentStatusObject>(
            `
                query($input: IdInput!) {
                    socialBillingDocument {
                        status(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialBillingDocumentCounts(): Promise<SocialBillingDocumentMonthCountObject[]> {
        return this.query<SocialBillingDocumentMonthCountObject[]>(
            `
                query {
                    socialBillingDocument {
                        counts {
                            serviceId
                            year
                            month
                            count
                        }
                    }
                }
            `,
        );
    }

    public deleteSocialBillingDocument(input: IdInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    socialBillingDocument {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public printSocialBillingDocument(input: SocialBillingDocumentPrintInput): Promise<string> {
        return this.mutate<string>(
            `
                mutation($input: SocialBillingDocumentPrintInput!) {
                    socialBillingDocument {
                        print(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public addMissingSocialBillingDocumentItems(input: SocialBillingDocumentAddMissingItemsInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: SocialBillingDocumentAddMissingItemsInput!) {
                    socialBillingDocument {
                        addMissingItems(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public removeSocialBillingDocumentItem(input: SocialBillingDocumentRemoveItemInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: SocialBillingDocumentRemoveItemInput!) {
                    socialBillingDocument {
                        removeItem(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialBillingReceipts(input: GridInput, fragment: string): Promise<SocialBillingReceiptGridObject> {
        return this.query<SocialBillingReceiptGridObject>(
            `
                query($input: GridInput!) {
                    socialBillingReceipt {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialPartialBilling(input: BilledOfferCreatePartialBillingInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: BilledOfferCreatePartialBillingInput!) {
                    billedOffer {
                        createPartialBilling(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialNewBilling(input: BilledOfferCreateBillingInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: BilledOfferCreateBillingInput!) {
                    billedOffer {
                        createBilling(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialRemainingBilling(
        input: BilledOfferCreateRemainingBillingInput,
        fragment: string = 'ok',
    ): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: BilledOfferCreateRemainingBillingInput!) {
                    billedOffer {
                        createRemainingBilling(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteSocialBillingReceipt(input: IdInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    socialBillingReceipt {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialBillingReceipt(input: SocialBillingReceiptCreateInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: SocialBillingReceiptCreateInput!) {
                    socialBillingReceipt {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public printSocialBillingReceipt(input: SocialBillingReceiptPrintInput): Promise<string> {
        return this.mutate<string>(
            `
                mutation($input: SocialBillingReceiptPrintInput!) {
                    socialBillingReceipt {
                        print(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public cancellationSocialBillingReceipt(input: SocialBillingReceiptCancellationInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: SocialBillingReceiptCancellationInput!) {
                    socialBillingReceipt {
                        cancellation(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public sendSocialBillingReceipt(input: SocialBillingReceiptSendInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: SocialBillingReceiptSendInput!) {
                    socialBillingReceipt {
                        send(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
